import type { FormikProps } from 'formik';
import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { isSameDay, isValid, parseISO, startOfDay } from 'date-fns';
import { fromZonedTime } from 'date-fns-tz';
import { useAnalyzeReceiptImage } from '@pflegenavi/frontend/api-nursing-home';
import { InputAdornment } from '@mui/material';
import { Iconify } from '@pflegenavi/web-components';

/**
 * Sparkle adornment marking a value auto-filled by receipt analysis, same
 * icon as the mobile app.
 */
export const ReceiptAutofillAdornment = (): JSX.Element => (
  <InputAdornment position="start">
    <Iconify icon="mdi:creation" sx={{ color: 'info.main' }} />
  </InputAdornment>
);

interface ReceiptAnalysisFormValues {
  receiptImageIds: string[];
  receiptDate: Date | null;
  receiptNumber: string;
}

export interface UseReceiptAnalysisAutofillProps<
  V extends ReceiptAnalysisFormValues
> {
  formik: FormikProps<V>;
  /**
   * Feature flag is on and the form is editable. When false, uploads never
   * trigger an analysis. (Whether a pre-existing image is auto-analyzed is
   * handled internally, based on the images present when the target opens.)
   */
  enabled: boolean;
  /**
   * The receipt date the dialog pre-fills: the Pliant transaction date for
   * groups with a Pliant transaction, null otherwise. A field is only
   * overwritten while it still holds its default.
   */
  defaultReceiptDate: Date | null;
  /** Re-arms the one-shot analysis when the dialog target changes. */
  resetKey: string;
}

export interface ReceiptAnalysisAutofill {
  analyzing: boolean;
  receiptDateAutofilled: boolean;
  receiptNumberAutofilled: boolean;
}

/**
 * True when the receipt date field is safe to auto-fill: it is empty, or it
 * still holds its default (the same calendar day). An empty required field is
 * always fillable — clearing it and uploading a new receipt should re-run OCR
 * even when a Pliant default date exists.
 */
export function isReceiptDateDefault(
  current: Date | null | undefined,
  defaultDate: Date | null
): boolean {
  if (!current) {
    return true;
  }
  if (!defaultDate) {
    return false;
  }
  return isSameDay(current, defaultDate);
}

/**
 * Parse an analysis ISO date (yyyy-MM-dd) into the same normalized form
 * FDatePicker produces, or null when invalid.
 */
export function parseAnalyzedReceiptDate(iso: string): Date | null {
  const parsed = parseISO(iso);
  if (!isValid(parsed)) {
    return null;
  }
  return fromZonedTime(startOfDay(parsed), 'UTC');
}

/**
 * Runs receipt image analysis on the first uploaded receipt image and
 * auto-fills the receipt date and receipt number fields — but only while they
 * still hold their defaults. Skips the request entirely when both fields were
 * already customized. Returns loading state and per-field highlight flags;
 * a manual edit changes the value away from the auto-filled one, which drops
 * the highlight.
 */
export function useReceiptAnalysisAutofill<
  V extends ReceiptAnalysisFormValues
>({
  formik,
  enabled,
  defaultReceiptDate,
  resetKey,
}: UseReceiptAnalysisAutofillProps<V>): ReceiptAnalysisAutofill {
  const analyze = useAnalyzeReceiptImage();
  const [analyzing, setAnalyzing] = useState(false);
  const [autofilled, setAutofilled] = useState<{
    date: Date | null;
    number: string | null;
  }>({ date: null, number: null });
  const firstImageId: string | undefined = formik.values.receiptImageIds[0];
  const { setFieldValue } = formik;
  const receiptDate = formik.values.receiptDate;
  const receiptNumber = formik.values.receiptNumber;

  // The image we've already analyzed. Starts at whatever the dialog opened
  // with, so a pre-existing receipt is left alone; any newly-uploaded image
  // (including one added after deleting all images) has a different id and so
  // gets analyzed.
  const analyzedIdRef = useRef(firstImageId);

  // Switching the dialog to another expense: forget the previous analysis.
  const prevKeyRef = useRef(resetKey);
  useEffect(() => {
    if (prevKeyRef.current !== resetKey) {
      prevKeyRef.current = resetKey;
      analyzedIdRef.current = firstImageId;
      setAutofilled({ date: null, number: null });
      setAnalyzing(false);
    }
  }, [resetKey, firstImageId]);

  // All images deleted: clear the fields this analysis filled so they're back
  // to their default (a later upload can fill them again). Manual edits, which
  // no longer match what we auto-filled, are left in place.
  useEffect(() => {
    if (
      firstImageId ||
      (autofilled.number === null && autofilled.date === null)
    ) {
      return;
    }
    if (autofilled.number !== null && receiptNumber === autofilled.number) {
      setFieldValue('receiptNumber', '');
    }
    if (
      autofilled.date !== null &&
      receiptDate?.getTime() === autofilled.date.getTime()
    ) {
      setFieldValue('receiptDate', defaultReceiptDate);
    }
    setAutofilled({ date: null, number: null });
  }, [
    firstImageId,
    autofilled,
    receiptNumber,
    receiptDate,
    defaultReceiptDate,
    setFieldValue,
  ]);

  useEffect(() => {
    if (!enabled || !firstImageId || firstImageId === analyzedIdRef.current) {
      return;
    }

    const numberIsDefault = receiptNumber.trim() === '';
    const dateIsDefault = isReceiptDateDefault(receiptDate, defaultReceiptDate);
    if (!numberIsDefault && !dateIsDefault) {
      // Both fields already customized — nothing to fill. Don't mark the image
      // analyzed, so if a field is later cleared this runs then.
      return;
    }
    analyzedIdRef.current = firstImageId;

    setAnalyzing(true);
    void (async () => {
      try {
        const result = await analyze(firstImageId);
        const next: { date: Date | null; number: string | null } = {
          date: null,
          number: null,
        };
        if (numberIsDefault && result.receiptNumber) {
          next.number = result.receiptNumber;
        }
        if (dateIsDefault && result.receiptDate) {
          next.date = parseAnalyzedReceiptDate(result.receiptDate);
        }
        if (next.number !== null) {
          setFieldValue('receiptNumber', next.number);
        }
        if (next.date !== null) {
          setFieldValue('receiptDate', next.date);
        }
        setAutofilled(next);
      } catch {
        // Analysis is best-effort; leave the form untouched on failure.
      } finally {
        setAnalyzing(false);
      }
    })();
  }, [
    enabled,
    firstImageId,
    defaultReceiptDate,
    receiptDate,
    receiptNumber,
    analyze,
    setFieldValue,
  ]);

  return {
    analyzing,
    receiptDateAutofilled:
      autofilled.date !== null &&
      receiptDate?.getTime() === autofilled.date.getTime(),
    receiptNumberAutofilled:
      autofilled.number !== null && receiptNumber === autofilled.number,
  };
}
